import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Brain, CalendarClock, Cloud, FlaskConical, Plug, SlidersHorizontal, Sparkles, Wand2 } from 'lucide-react';
import { useFlag } from '@/contexts/flags-context';
import { AgentOrb } from '@/components/agent-avatar/agent-orb';
import { AgentOrbActivityProvider, useAgentOrbActivity } from '@/components/agent-avatar/agent-orb-activity';
import './agent-settings.css';

const sections = [
  { key: 'behavior', title: 'Personalidad', description: 'Qué hace y cómo se comporta.', icon: Sparkles },
  { key: 'connections', title: 'Conexiones', description: 'Las herramientas que puede usar.', icon: Plug },
  { key: 'rituals', title: 'Rituales', description: 'Cuándo trabaja por su cuenta.', icon: CalendarClock },
  { key: 'memory', title: 'Memoria', description: 'Lo que recuerda y aprende.', icon: Brain },
  { key: 'test', title: 'Probar', description: 'Conversá y comprobá cómo responde.', icon: FlaskConical },
  { key: 'advanced', title: 'Avanzado', description: 'Modelo, límites y alcance de datos.', icon: SlidersHorizontal },
];

/** The rail avatar follows what the open page reports (e.g. a Probar conversation). */
function FrameOrb({ id, avatar, status }: { id: string; avatar?: unknown; status?: string }) {
  const activity = useAgentOrbActivity();
  const avatars = useFlag('agent-avatars');
  if (!avatars) return <span className="beta-settings__avatar"><Cloud size={22} /></span>;
  const resting = status === 'paused' || status === 'archived' ? 'sleeping' : 'idle';
  return <AgentOrb avatar={avatar} agentId={id} size={46} state={activity.state ?? resting} toolIcon={activity.toolIcon} label="" />;
}

/** New composition; all editor controllers remain shared with the existing site. */
export function AgentSettingsFrame({ id, name, status, avatar, active, search, onAssistant, setup, notice, children }: {
  id: string; name: string; status?: string; avatar?: unknown; active: string; search: string; onAssistant: () => void;
  setup?: ReactNode; notice?: ReactNode; children: ReactNode;
}) {
  const section = sections.find((item) => item.key === active) ?? sections[0];
  return (
    <AgentOrbActivityProvider>
    <div className="beta-settings">
      <aside className="beta-settings__rail" aria-label="Configuración del agente">
        <div className="beta-settings__identity">
          <FrameOrb id={id} avatar={avatar} status={status} />
          <div><strong>{name || 'Cargando agente…'}</strong><span>Agente cloud{status ? ` · ${status === 'active' ? 'Habilitado' : status === 'paused' ? 'Pausado' : 'Archivado'}` : ''}</span></div>
        </div>
        <nav aria-label="Opciones del agente">
          {sections.map(({ key, title, icon: Icon }) => <Link key={key} to={`/agents/${encodeURIComponent(id)}/${key}${search}`} aria-current={active === key ? 'page' : undefined} className={`beta-settings__option ${active === key ? 'is-selected' : ''}`}><Icon size={18} /><span>{title}</span></Link>)}
        </nav>
        <button type="button" className="aw-btn aw-btn--line beta-settings__assistant" onClick={onAssistant}><Wand2 size={16} /> Pedir ayuda al asistente</button>
        {setup && <details className="beta-settings__setup"><summary>Estado de preparación</summary>{setup}</details>}
      </aside>
      <div className="beta-settings__content">
        <header className="beta-settings__heading"><span className="beta-settings__eyebrow">Configuración</span><h1>{section.title}</h1><p>{section.description}</p></header>
        {notice}
        <div className="beta-settings__editor">{children}</div>
      </div>
    </div>
    </AgentOrbActivityProvider>
  );
}
